import React from 'react';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import CandlestickChartRoundedIcon from '@mui/icons-material/CandlestickChartRounded';
import AnalyticsRoundedIcon from '@mui/icons-material/AnalyticsRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import SettingsSuggestRoundedIcon from '@mui/icons-material/SettingsSuggestRounded';
import GetAppRoundedIcon from '@mui/icons-material/GetAppRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import MonitorHeartRoundedIcon from '@mui/icons-material/MonitorHeartRounded';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import {
  Box,
  Chip,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';

import AdminOverviewPage from './AdminOverviewPage';
import JobAdminPage from './JobAdminPage';
import ExportAdminPage from './ExportAdminPage';
import PatternMarketDataAdminPage from './PatternMarketDataAdminPage';
import PatternReplayAdminPage from './PatternReplayAdminPage';
import PatternImageQueueMonitoringPage from './PatternImageQueueMonitoringPage';
import HealthAdminPage from './HealthAdminPage';
import MailCenterAdminPage from './MailCenterAdminPage';
import PatternPerformanceAdminPage from './PatternPerformanceAdminPage';
import PatternScannerPageViewsAdminPage from './PatternScannerPageViewsAdminPage';
import SystemSettingsAdminPage from './SystemSettingsAdminPage';

function readStoredUser() {
  try {
    return JSON.parse(localStorage.getItem('user')) || null;
  } catch {
    return null;
  }
}

const menu = [
  {
    label: 'Vue d’ensemble',
    path: '/admin/overview',
    description: 'Synthèse des contacts non lus, des notifications et des incidents récents.',
    icon: <DashboardRoundedIcon />,
  },
  {
    label: 'Mail Center',
    path: '/admin/mail-center',
    description: 'Contacts, historique des emails sortants et notifications administratives.',
    icon: <MailOutlineRoundedIcon />,
  },
  {
    label: 'Jobs',
    path: '/admin/job',
    description: 'Declencheurs manuels pour les traitements backend.',
    icon: <SettingsSuggestRoundedIcon />,
  },
  {
    label: 'Monitoring Queue',
    path: '/admin/pattern-image-queue',
    description: 'Suivi temps réel de la génération d\'images patterns.',
    icon: <GetAppRoundedIcon />,
  },
  {
    label: 'Export CSV',
    path: '/admin/export',
    description: 'Snapshots directs vers src/main/resources/import/.',
    icon: <SaveRoundedIcon />,
  },
  {
    label: 'Pattern Market Data',
    path: '/admin/pattern-market-data',
    description: 'Bootstrap historique, synchro et snapshots CSV.',
    icon: <CandlestickChartRoundedIcon />,
  },
  {
    label: 'Pattern Performance',
    path: '/admin/pattern-performance',
    description: 'Benchmark des versions du moteur et suivi de resolution des detections patterns.',
    icon: <AnalyticsRoundedIcon />,
  },
  {
    label: 'Pattern Replay',
    path: '/admin/pattern-replay',
    description: 'Relecture d une fenetre exacte ou d un offset historique.',
    icon: <ReplayRoundedIcon />,
  },
  {
    label: 'Logs Scanner',
    path: '/admin/pattern-scanner-views',
    description: 'Historique des pages Pattern Scanner consultées par les utilisateurs connectés.',
    icon: <HistoryRoundedIcon />,
  },
  {
    label: 'Health',
    path: '/admin/health',
    description: 'Vérifie la disponibilité de CCXT et du moteur Python.',
    icon: <MonitorHeartRoundedIcon />,
  },
  {
    label: 'Paramètres',
    path: '/admin/settings',
    description: 'Réglages système du Mail Center et bascules administratives.',
    icon: <TuneRoundedIcon />,
  },
];

const AdminPage = () => {
  const location = useLocation();
  const storedUser = readStoredUser();

  if (!storedUser || String(storedUser.role || '').toUpperCase() !== 'ADMIN') {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  const currentSection = menu.find((item) => location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)) || menu[0];

  return (
    <Box sx={{ p: 3, background: 'linear-gradient(180deg, #efe8dc 0%, #edf3f6 100%)', minHeight: '100%' }}>
      <Stack spacing={3}>
        <Paper
          sx={{
            width: '100%',
            p: 3,
            borderRadius: 5,
            background: 'linear-gradient(135deg, #17323c 0%, #355b63 100%)',
            color: '#f7f3ea',
          }}
        >
          <Stack direction={{ xs: 'column', xl: 'row' }} spacing={3} justifyContent="space-between" alignItems={{ xs: 'flex-start', xl: 'stretch' }}>
            <Box sx={{ width: { xs: '100%', xl: 320 }, flexShrink: 0 }}>
              <Chip
                icon={<AdminPanelSettingsRoundedIcon sx={{ color: '#f7f3ea !important' }} />}
                label="Admin Console"
                sx={{ bgcolor: 'rgba(247,243,234,0.14)', color: '#f7f3ea', fontWeight: 700, mb: 1.5 }}
              />
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 1.1 }}>
                Pilotage admin
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(247,243,234,0.82)', lineHeight: 1.7, mb: 0 }}>
                Navigation rapide pour le Mail Center, les réglages système et les outils techniques déjà présents dans l’administration EcotoneAI.
              </Typography>
            </Box>

            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 4,
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(247,243,234,0.1)',
                width: { xs: '100%', xl: 280 },
                flexShrink: 0,
              }}
            >
              <Typography variant="caption" sx={{ color: 'rgba(247,243,234,0.72)', display: 'block', mb: 0.4 }}>
                Section active
              </Typography>
              <Typography variant="body2" sx={{ color: '#f3c65c', fontWeight: 800, mb: 0.5 }}>
                {currentSection.label}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(247,243,234,0.78)', lineHeight: 1.6 }}>
                {currentSection.description}
              </Typography>
            </Paper>
          </Stack>

          <Box
            sx={{
              mt: 3,
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(2, minmax(0, 1fr))',
                xl: 'repeat(3, minmax(0, 1fr))',
              },
              gap: 1.2,
            }}
          >
            {menu.map((item) => {
              const active = location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
              return (
                <Box
                  key={item.path}
                  component={NavLink}
                  to={item.path}
                  sx={{ textDecoration: 'none' }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      borderRadius: 4,
                      background: active ? 'rgba(247,243,234,0.14)' : 'rgba(255,255,255,0.04)',
                      border: active ? '1px solid rgba(247,243,234,0.28)' : '1px solid rgba(247,243,234,0.08)',
                      color: '#f7f3ea',
                      transition: 'transform 120ms ease, background 120ms ease, border-color 120ms ease',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        background: 'rgba(247,243,234,0.1)',
                        borderColor: 'rgba(247,243,234,0.22)',
                      },
                    }}
                  >
                    <Stack direction="row" spacing={1.4} alignItems="flex-start">
                      <Box
                        sx={{
                          width: 42,
                          height: 42,
                          borderRadius: 3,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: active ? 'rgba(247,243,234,0.16)' : 'rgba(255,255,255,0.06)',
                          color: active ? '#f3c65c' : '#f7f3ea',
                          flexShrink: 0,
                        }}
                      >
                        {item.icon}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#f7f3ea', mb: 0.25 }}>
                          {item.label}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(247,243,234,0.74)', lineHeight: 1.5 }}>
                          {item.description}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Box>
              );
            })}
          </Box>
        </Paper>

        <Box sx={{ flex: 1, minWidth: 0 }}>
        <Routes>
          <Route index element={<Navigate to="overview" replace />} />
          <Route path="overview" element={<AdminOverviewPage />} />
          <Route path="mail-center" element={<MailCenterAdminPage />} />
          <Route path="job" element={<JobAdminPage />} />
          <Route path="pattern-image-queue" element={<PatternImageQueueMonitoringPage />} />
          <Route path="export" element={<ExportAdminPage />} />
          <Route path="pattern-market-data" element={<PatternMarketDataAdminPage />} />
          <Route path="pattern-performance" element={<PatternPerformanceAdminPage />} />
          <Route path="pattern-replay" element={<PatternReplayAdminPage />} />
          <Route path="pattern-scanner-views" element={<PatternScannerPageViewsAdminPage />} />
          <Route path="health" element={<HealthAdminPage />} />
          <Route path="settings" element={<SystemSettingsAdminPage />} />
        </Routes>
        </Box>
      </Stack>
    </Box>
  );
};

export default AdminPage;
